import { useQuery } from "@tanstack/react-query";
import { startOfDay } from "date-fns";

import { supabase } from "@/integrations/supabase/client";
import { attachDocumentBreadcrumbs } from "@/stores/pm/repository";
import type { LinkedDocument } from "@/domains/pm/types";
import type { FolderPathSegment } from "@/domains/documents/utils/folderPathResolver";

export interface UploadedTodayDocument {
  id: number;
  filename: string;
  file_path: string;
  content_type: string | null;
  size: number | null;
  state: string | null;
  created_at: string;
  uploaderName: string;
  /** Interactive project → folder → filename path for the hover tooltip.
   * Null when the document has no project/folder context. */
  breadcrumbSegments: FolderPathSegment[] | null;
}

/**
 * Documents uploaded today in one project — or across the whole building
 * when `projectId` is null ("All projects" scope) — the project dashboard's
 * "Uploaded today" feed. Same select/join shape as the document activity
 * queries (`user_profiles!documents_created_by_fk` resolves the uploader);
 * folder-path breadcrumbs are batch-resolved via the same
 * `attachDocumentBreadcrumbs` the message board and PM linked docs use.
 */
export function useUploadedToday(buildingId: string | undefined, projectId: number | null) {
  return useQuery<UploadedTodayDocument[]>({
    queryKey: ["uploaded-today", buildingId, projectId],
    enabled: !!buildingId,
    staleTime: 60_000,
    queryFn: async () => {
      let query = supabase
        .from("documents")
        .select(
          `
          id,
          filename,
          file_path,
          content_type,
          size,
          state,
          created_at,
          project_id,
          folder_id,
          user_profiles!documents_created_by_fk (
            first_name,
            last_name,
            email
          )
        `
        )
        .eq("building_id", Number(buildingId))
        .eq("hidden", false)
        .is("deleted_at", null)
        .gte("created_at", startOfDay(new Date()).toISOString())
        .order("created_at", { ascending: false });
      if (projectId != null) {
        query = query.eq("project_id", projectId);
      }
      const { data, error } = await query;
      if (error) throw error;

      // attachDocumentBreadcrumbs works on the PM LinkedDocument shape and
      // writes `breadcrumbSegments` in place — feed it a compatible view of
      // the rows, then read the segments back per document below.
      const linkedDocs: LinkedDocument[] = (data ?? []).map((doc) => ({
        id: doc.id,
        filename: doc.filename,
        state: doc.state ?? "",
        content_type: doc.content_type,
        file_path: doc.file_path,
        size: doc.size,
        created_at: doc.created_at,
        parent_document_id: null,
        version: null,
        project_id: doc.project_id,
        folder_id: doc.folder_id,
      }));
      await attachDocumentBreadcrumbs(linkedDocs);
      const segmentsByDocId = new Map<number, FolderPathSegment[] | null>(
        linkedDocs.map((doc) => [Number(doc.id), doc.breadcrumbSegments ?? null])
      );

      return (data ?? []).map((doc) => {
        const profile = doc.user_profiles;
        const uploaderName =
          profile?.first_name || profile?.last_name
            ? [profile.first_name, profile.last_name].filter(Boolean).join(" ")
            : (profile?.email ?? "");
        return {
          id: doc.id,
          filename: doc.filename,
          file_path: doc.file_path,
          content_type: doc.content_type,
          size: doc.size,
          state: doc.state,
          created_at: doc.created_at,
          uploaderName,
          breadcrumbSegments: segmentsByDocId.get(doc.id) ?? null,
        };
      });
    },
  });
}
